import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import SprsGauge from "@/components/SprsGauge";
import { ShieldCheck, Loader2, ArrowRight, AlertTriangle, Lock } from "lucide-react";

export default function ScoreTeaser() {
  const [form, setForm] = useState({ company_name: "", email: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.content.trim().length < 80) return setError("Paste at least a paragraph of your policy or IT documentation.");
    setLoading(true);
    try {
      const { data } = await api.post("/public/score-teaser", form);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <ShieldCheck className="h-7 w-7 text-brand-accent" />
            <span className="font-display font-black text-xl tracking-tight">CMMC Shield</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-brand" data-testid="teaser-login">Sign in</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="overline mb-3">Free · No credit card · 30 seconds</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">What's your SPRS score right now?</h1>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">Paste any of your IT/security policy text. Our AI estimates your NIST 800-171 SPRS score against the highest-impact controls — instantly.</p>
        </div>

        {!result ? (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md shadow-sm p-7 max-w-2xl mx-auto" data-testid="teaser-form">
            {error && <div className="mb-4 text-sm text-danger bg-danger-bg border border-red-200 rounded px-3 py-2" data-testid="teaser-error">{error}</div>}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Company name</label>
                <input value={form.company_name} onChange={upd("company_name")} required data-testid="teaser-company"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Work email</label>
                <input type="email" value={form.email} onChange={upd("email")} required data-testid="teaser-email"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
              </div>
            </div>
            <label className="text-sm font-medium text-slate-700">Paste policy / IT documentation</label>
            <textarea value={form.content} onChange={upd("content")} rows={8} required data-testid="teaser-content"
              placeholder="e.g. We enforce MFA via Azure AD for all remote and privileged accounts. Endpoints run Microsoft Defender with real-time scanning. Audit logs are forwarded to our SIEM and retained 90 days..."
              className="w-full mt-1 mb-4 px-3 py-2 border border-slate-300 rounded-md font-mono text-xs focus:ring-2 focus:ring-brand-accent focus:outline-none" />
            <button disabled={loading} data-testid="teaser-submit"
              className="w-full bg-brand text-white rounded-md py-3 font-medium hover:bg-brand-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Scoring your evidence…</> : <>Get my SPRS score <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="text-xs text-slate-400 mt-3 text-center">We never store your document — only the score estimate.</p>
          </form>
        ) : (
          <div className="max-w-2xl mx-auto" data-testid="teaser-result">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-7 flex flex-col items-center">
              <SprsGauge score={result.estimated_score} />
              <div className="text-sm text-slate-500 -mt-1 mb-4">Estimated SPRS score · {result.sampled_met}/{result.sampled_total} high-impact controls met</div>
              {result.estimated_score < 88 && (
                <div className="w-full flex items-start gap-2 bg-warning-bg border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-800 mb-4" data-testid="teaser-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  Below the threshold most primes require. You likely can't pass a CMMC Level 2 assessment today.
                </div>
              )}
              <div className="w-full">
                <div className="overline mb-2">Top gaps detected</div>
                {result.top_gaps.length === 0 ? (
                  <div className="text-sm text-slate-500">No major gaps in the sampled controls — nice.</div>
                ) : result.top_gaps.map((g) => (
                  <div key={g.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm" data-testid={`gap-${g.id}`}>
                    <div><span className="font-mono text-xs text-slate-500 mr-2">{g.id}</span>{g.requirement.slice(0, 70)}…</div>
                    <span className="font-mono text-xs text-danger shrink-0">-{g.sprs_points}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-brand text-white rounded-md p-7 mt-5 text-center">
              <Lock className="h-6 w-6 text-brand-accent mx-auto mb-3" />
              <h3 className="font-display font-bold text-xl tracking-tight">This is just {result.sampled_total} of {result.full_objectives}+ objectives.</h3>
              <p className="text-slate-300 text-sm mt-2 mb-5 max-w-md mx-auto">Create a free account to run the full gap analysis, get cited evidence for every control, and auto-generate your POA&M and policies.</p>
              <Link to="/register" data-testid="teaser-cta" className="inline-flex items-center gap-2 bg-white text-brand px-6 py-3 rounded-md font-medium hover:bg-slate-100 transition-colors">
                See my full score <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
